'use client';

import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import { Grid3X3, List, Filter, Search, Edit3, Trash2, CheckCircle, Clock, AlertCircle, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { ApiErrorMessage } from '../../components/ui/ErrorBoundary';

interface ContentItem {
  id: string;
  day: number;
  platform: string;
  format: string;
  title: string;
  preview: string;
  status: 'entwurf' | 'bereit' | 'geplant' | 'veröffentlicht';
  words: number;
  created: string;
}

const mockContent: ContentItem[] = [
  { id: 'c-001', day: 1, platform: 'LI', format: 'Experten-Artikel', title: 'Warum der Mittelstand KI falsch versteht', preview: 'Die meisten Unternehmen im Mittelstand betrachten KI als Bedrohung. Das ist ein Fehler, der sie teuer zu stehen kommen wird...', status: 'bereit', words: 420, created: '02.04.2026' },
  { id: 'c-002', day: 1, platform: 'IG', format: 'Reel-Script', title: 'Behind the Scenes: Unser Alltag', preview: 'Hook: "Was passiert wirklich hinter den Kulissen eines Mittelstandsunternehmens?" [Schnitt auf Büro-Szene]...', status: 'entwurf', words: 180, created: '02.04.2026' },
  { id: 'c-003', day: 2, platform: 'TT', format: 'POV-Script', title: 'POV: Du bist der einzige ohne Content-Strategie', preview: 'Hook (0-3 Sek): Kamera auf Gesicht. "POV: Alle deine Konkurrenten posten täglich. Du nicht." [Dramatische Pause]...', status: 'bereit', words: 220, created: '02.04.2026' },
  { id: 'c-004', day: 2, platform: 'X', format: 'Viraler Thread', title: '5 Fehler die jeder Mittelständler bei Content macht', preview: '1/ Die meisten Mittelständler machen Content-Marketing falsch. Hier sind 5 Fehler, die ich immer wieder sehe (Thread)...', status: 'geplant', words: 340, created: '01.04.2026' },
  { id: 'c-005', day: 3, platform: 'FB', format: 'Satire-Post', title: 'Das tägliche Meeting-Bingo im deutschen Büro', preview: 'Heute wieder Meeting-Bingo gespielt 🎯 "Wir müssen das mal offline besprechen" — CHECK! "Synergien nutzen" — CHECK!...', status: 'veröffentlicht', words: 150, created: '01.04.2026' },
  { id: 'c-006', day: 3, platform: 'LI', format: 'Thought Leadership', title: 'Die Zukunft der Arbeit: Was 2026 wirklich bedeutet', preview: 'Ich höre viele Prognosen über die Zukunft der Arbeit. Die meisten sind falsch. Hier ist, was ich wirklich glaube...', status: 'bereit', words: 580, created: '01.04.2026' },
  { id: 'c-007', day: 4, platform: 'IG', format: '10-Slide Carousel', title: '10 Wahrheiten über Content im Mittelstand', preview: 'Slide 1: "10 Dinge, die niemand dir über Content-Marketing sagt" | Slide 2: "Du brauchst keine perfekte Kamera"...', status: 'entwurf', words: 290, created: '31.03.2026' },
  { id: 'c-008', day: 4, platform: 'TT', format: 'Rebel-Style Video', title: 'Rebel-Style: Warum Standard-Marketing tot ist', preview: 'Hook: [Zerreiße ein Blatt Papier mit "Standard-Marketing" drauf] "Das hier ist dein Marketing-Plan von 2020."...', status: 'bereit', words: 200, created: '31.03.2026' },
  { id: 'c-009', day: 5, platform: 'FB', format: 'Community-Post', title: 'Community-Frage: Wie plant ihr euren Content?', preview: 'Ehrliche Frage an die Community: Wie plant ihr euren Content? Spontan? Wöchentlich? Mit Tool? Ohne Plan?...', status: 'geplant', words: 120, created: '31.03.2026' },
  { id: 'c-010', day: 5, platform: 'BLOG', format: 'SEO-Artikel', title: 'KI im Mittelstand: 5 Chancen die du 2026 nicht verpassen solltest', preview: 'Künstliche Intelligenz ist längst kein Zukunftsthema mehr. Für den deutschen Mittelstand bieten sich 2026 konkrete Chancen...', status: 'bereit', words: 1840, created: '30.03.2026' },
];

const statusConfig: Record<string, { label: string; icon: React.ReactNode; cls: string }> = {
  entwurf: { label: 'Entwurf', icon: <Edit3 size={11} />, cls: 'bg-surface-2 text-muted border-border' },
  bereit: { label: 'Bereit', icon: <CheckCircle size={11} />, cls: 'bg-green-950/30 text-green-400 border-green-800/40' },
  geplant: { label: 'Geplant', icon: <Clock size={11} />, cls: 'bg-yellow-950/30 text-yellow-400 border-yellow-800/40' },
  veröffentlicht: { label: 'Veröffentlicht', icon: <AlertCircle size={11} />, cls: 'bg-blue-950/30 text-blue-400 border-blue-800/40' },
};

const platformBadge: Record<string, string> = {
  LI: 'platform-badge-li',
  IG: 'platform-badge-ig',
  X: 'platform-badge-x',
  FB: 'platform-badge-fb',
  TT: 'platform-badge-tt',
  BLOG: 'platform-badge-blog',
};

/* ── Skeleton card for grid view ── */
function GridCardSkeleton() {
  return (
    <div className="punk-card p-4 animate-pulse">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-5 w-10 rounded bg-surface-2" />
        <div className="h-4 w-24 rounded bg-surface-2" />
        <div className="h-5 w-14 rounded bg-surface-2 ml-auto" />
      </div>
      <div className="space-y-2 mb-3">
        <div className="h-3 w-3/4 rounded bg-surface-2" />
        <div className="h-3 w-full rounded bg-surface-2" />
        <div className="h-3 w-5/6 rounded bg-surface-2" />
        <div className="h-3 w-2/3 rounded bg-surface-2" />
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex gap-2">
          <div className="h-3 w-10 rounded bg-surface-2" />
          <div className="h-3 w-16 rounded bg-surface-2" />
        </div>
        <div className="flex gap-1">
          <div className="w-6 h-6 rounded bg-surface-2" />
          <div className="w-6 h-6 rounded bg-surface-2" />
          <div className="w-6 h-6 rounded bg-surface-2" />
        </div>
      </div>
    </div>
  );
}

/* ── Skeleton row for list view ── */
function ListRowSkeleton() {
  return (
    <div className="flex items-start gap-4 p-4 animate-pulse">
      <div className="w-4 h-4 rounded border border-border flex-shrink-0 mt-0.5 bg-surface-2" />
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="h-5 w-10 rounded bg-surface-2" />
        <div className="h-3 w-10 rounded bg-surface-2" />
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="h-3 w-3/4 rounded bg-surface-2" />
        <div className="h-3 w-1/2 rounded bg-surface-2" />
      </div>
      <div className="h-5 w-16 rounded bg-surface-2 flex-shrink-0" />
      <div className="flex gap-1 flex-shrink-0">
        <div className="w-6 h-6 rounded bg-surface-2" />
        <div className="w-6 h-6 rounded bg-surface-2" />
      </div>
    </div>
  );
}

export default function BackstagePage() {
  const pathname = usePathname();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterPlatform, setFilterPlatform] = useState('alle');
  const [filterStatus, setFilterStatus] = useState('alle');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const platforms = ['alle', 'LI', 'IG', 'TT', 'FB', 'X', 'BLOG'];
  const statuses = ['alle', 'entwurf', 'bereit', 'geplant', 'veröffentlicht'];

  /* Simulate initial data load */
  useEffect(() => {
    setLoadError(null);
    const t = setTimeout(() => setLoading(false), 900);
    return () => clearTimeout(t);
  }, []);

  /* Simulate filter re-fetch */
  const applyFilter = (fn: () => void) => {
    setLoading(true);
    setLoadError(null);
    fn();
    setTimeout(() => setLoading(false), 500);
  };

  const filtered = mockContent.filter((item) => {
    const matchPlatform = filterPlatform === 'alle' || item.platform === filterPlatform;
    const matchStatus = filterStatus === 'alle' || item.status === filterStatus;
    const matchSearch = !searchQuery || item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.preview.toLowerCase().includes(searchQuery.toLowerCase());
    return matchPlatform && matchStatus && matchSearch;
  });

  const toggleSelect = (id: string) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const handleBulkExport = () => {
    if (selected.length === 0) { toast.error('Keine Beiträge ausgewählt.'); return; }
    toast.success(`${selected.length} Beiträge werden exportiert…`);
    setSelected([]);
  };

  return (
    <AppLayout currentPath={pathname}>
      <section className="p-6" aria-label="Backstage Grid">
        {/* Header */}
        <header className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Grid3X3 size={18} className="text-accent" aria-hidden="true" />
              <h1 className="text-lg font-800 text-white uppercase tracking-widest">Backstage Grid</h1>
            </div>
            <p className="text-xs text-muted">Alle generierten Inhalte auf einen Blick — filtern, prüfen, exportieren</p>
          </div>
          <div className="flex gap-2">
            {selected.length > 0 && (
              <button onClick={handleBulkExport} className="btn-primary text-xs">
                <Download size={12} />
                {selected.length} exportieren
              </button>
            )}
            <Link href="/content-generator" className="btn-ghost text-xs">
              <Filter size={12} />
              Neu generieren
            </Link>
          </div>
        </header>

        {/* Filters */}
        <div className="punk-card p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-48">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true" />
              <input
                type="search"
                className="input-punk pl-8 text-xs py-2"
                placeholder="Beiträge durchsuchen…"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); }}
                aria-label="Beiträge durchsuchen"
              />
            </div>

            {/* Platform filter */}
            <div className="flex gap-1 flex-wrap" role="group" aria-label="Nach Plattform filtern">
              {platforms.map((p) => (
                <button
                  key={p}
                  onClick={() => applyFilter(() => setFilterPlatform(p))}
                  className={`text-xs mono px-2.5 py-1.5 rounded border transition-all ${
                    filterPlatform === p ? 'bg-accent text-black border-accent font-700' : 'border-border text-muted hover:border-accent/40 hover:text-white'
                  }`}
                  aria-pressed={filterPlatform === p}
                  aria-label={`Plattform ${p === 'alle' ? 'Alle' : p} ${filterPlatform === p ? '(aktiv)' : ''}`}
                >
                  {p === 'alle' ? 'Alle' : p}
                </button>
              ))}
            </div>

            {/* Status filter */}
            <div className="flex gap-1 flex-wrap" role="group" aria-label="Nach Status filtern">
              {statuses.map((s) => (
                <button
                  key={s}
                  onClick={() => applyFilter(() => setFilterStatus(s))}
                  className={`text-xs px-2.5 py-1.5 rounded border transition-all capitalize ${
                    filterStatus === s ? 'bg-accent text-black border-accent font-700' : 'border-border text-muted hover:border-accent/40 hover:text-white'
                  }`}
                  aria-pressed={filterStatus === s}
                  aria-label={`Status ${s === 'alle' ? 'Alle' : s} ${filterStatus === s ? '(aktiv)' : ''}`}
                >
                  {s === 'alle' ? 'Alle' : s}
                </button>
              ))}
            </div>

            {/* View toggle */}
            <div className="flex gap-1 ml-auto" role="group" aria-label="Ansicht wechseln">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded border transition-all ${viewMode === 'grid' ? 'border-accent text-accent' : 'border-border text-muted hover:text-white'}`}
                aria-label="Rasteransicht"
                aria-pressed={viewMode === 'grid'}
              >
                <Grid3X3 size={14} aria-hidden="true" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded border transition-all ${viewMode === 'list' ? 'border-accent text-accent' : 'border-border text-muted hover:text-white'}`}
                aria-label="Listenansicht"
                aria-pressed={viewMode === 'list'}
              >
                <List size={14} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <p
              className="text-xs text-muted mono"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {loading ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 size={11} className="animate-spin text-accent" aria-hidden="true" />
                  Lade Beiträge…
                </span>
              ) : (
                `${filtered.length} Beiträge · ${selected.length > 0 ? `${selected.length} ausgewählt` : 'Nichts ausgewählt'}`
              )}
            </p>
          </div>
        </div>

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3"
            role="list"
            aria-label="Content-Beiträge Rasteransicht"
          >
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <GridCardSkeleton key={`sk-${i}`} />)
              : loadError
              ? <div className="col-span-full">
                  <ApiErrorMessage
                    title="Inhalte konnten nicht geladen werden"
                    description={loadError}
                    onRetry={() => { setLoadError(null); setLoading(true); setTimeout(() => setLoading(false), 900); }}
                  />
                </div>
              : filtered.map((item) => {
                  const st = statusConfig[item.status] ?? statusConfig.entwurf;
                  const isSelected = selected.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`punk-card p-4 cursor-pointer transition-all ${isSelected ? 'border-accent neon-glow' : 'hover:border-border-2'}`}
                      onClick={() => toggleSelect(item.id)}
                      role="listitem"
                      aria-label={`Beitrag: ${item.title} — Plattform ${item.platform}, Status ${st.label}${isSelected ? ', ausgewählt' : ''}`}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelect(item.id); } }}
                      aria-pressed={isSelected}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span
                          className={`status-badge ${platformBadge[item.platform] ?? ''}`}
                          aria-label={`Plattform: ${item.platform}`}
                        >
                          {item.platform}
                        </span>
                        <span className="text-xs text-muted">{item.format}</span>
                        <span
                          className={`status-badge border ml-auto ${st.cls}`}
                          role="status"
                          aria-label={`Status: ${st.label}`}
                        >
                          <span aria-hidden="true">{st.icon}</span>
                          {st.label}
                        </span>
                      </div>
                      <h3 className="text-xs font-600 text-white leading-snug mb-2 line-clamp-2">{item.title}</h3>
                      <p className="text-xs text-muted leading-relaxed line-clamp-3">{item.preview}</p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-2">
                          <span className="mono text-xs text-muted">Tag {item.day}</span>
                          <span className="text-muted" aria-hidden="true">·</span>
                          <span className="mono text-xs text-muted">{item.words} Wörter</span>
                        </div>
                        <div className="flex gap-1" role="group" aria-label={`Aktionen für "${item.title}"`}>
                          <button
                            onClick={(e) => { e.stopPropagation(); toast.success('Im Editor geöffnet'); }}
                            className="p-1.5 text-muted hover:text-accent transition-colors focus:outline-none focus:ring-1 focus:ring-accent rounded"
                            aria-label={`"${item.title}" im Editor öffnen`}
                          >
                            <Edit3 size={12} aria-hidden="true" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); toast.success('Beitrag exportiert'); }}
                            className="p-1.5 text-muted hover:text-accent transition-colors focus:outline-none focus:ring-1 focus:ring-accent rounded"
                            aria-label={`"${item.title}" exportieren`}
                          >
                            <Download size={12} aria-hidden="true" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); toast.error('Beitrag gelöscht'); }}
                            className="p-1.5 text-muted hover:text-red-400 transition-colors focus:outline-none focus:ring-1 focus:ring-red-400 rounded"
                            aria-label={`"${item.title}" löschen`}
                          >
                            <Trash2 size={12} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div
            className="punk-card divide-y divide-border"
            role="list"
            aria-label="Content-Beiträge Listenansicht"
          >
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <ListRowSkeleton key={`sk-list-${i}`} />)
              : loadError
              ? <div className="p-4">
                  <ApiErrorMessage
                    title="Inhalte konnten nicht geladen werden"
                    description={loadError}
                    onRetry={() => { setLoadError(null); setLoading(true); setTimeout(() => setLoading(false), 900); }}
                  />
                </div>
              : filtered.map((item) => {
                  const st = statusConfig[item.status] ?? statusConfig.entwurf;
                  const isSelected = selected.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`flex items-start gap-4 p-4 cursor-pointer transition-all hover:bg-surface ${isSelected ? 'bg-accent/5' : ''}`}
                      onClick={() => toggleSelect(item.id)}
                      role="listitem"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelect(item.id); } }}
                      aria-label={`Beitrag: ${item.title} — ${item.platform}, ${st.label}${isSelected ? ', ausgewählt' : ''}`}
                    >
                      <div
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${isSelected ? 'bg-accent border-accent' : 'border-border'}`}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={`"${item.title}" auswählen`}
                      >
                        {isSelected && <span className="text-black text-xs font-700" aria-hidden="true">✓</span>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className={`status-badge ${platformBadge[item.platform] ?? ''}`}
                          aria-label={`Plattform: ${item.platform}`}
                        >
                          {item.platform}
                        </span>
                        <span className="mono text-xs text-muted">Tag {item.day}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-600 text-white truncate">{item.title}</p>
                        <p className="text-xs text-muted truncate">{item.format} · {item.words} Wörter</p>
                      </div>
                      <span
                        className={`status-badge border flex-shrink-0 ${st.cls}`}
                        role="status"
                        aria-label={`Status: ${st.label}`}
                      >
                        <span aria-hidden="true">{st.icon}</span>
                        {st.label}
                      </span>
                      <div className="flex gap-1 flex-shrink-0" role="group" aria-label={`Aktionen für "${item.title}"`}>
                        <button
                          onClick={(e) => { e.stopPropagation(); toast.success('Im Editor geöffnet'); }}
                          className="p-1.5 text-muted hover:text-accent transition-colors focus:outline-none focus:ring-1 focus:ring-accent rounded"
                          aria-label={`"${item.title}" im Editor öffnen`}
                        >
                          <Edit3 size={12} aria-hidden="true" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); toast.success('Exportiert'); }}
                          className="p-1.5 text-muted hover:text-accent transition-colors focus:outline-none focus:ring-1 focus:ring-accent rounded"
                          aria-label={`"${item.title}" exportieren`}
                        >
                          <Download size={12} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  );
                })}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="punk-card p-12 text-center">
            <Grid3X3 size={32} className="text-muted mx-auto mb-3" />
            <p className="text-sm text-white font-600 mb-1">Keine Beiträge gefunden</p>
            <p className="text-xs text-muted">Passe deine Filter an oder generiere neuen Content.</p>
          </div>
        )}
      </section>
    </AppLayout>
  );
}
